<template>
  <div class="form-group" :class="classFormGroup">
    <label v-if="label" style="width: 100%">{{ label }}
      <span v-if="required" style="color: red;"> *</span>
      <span v-if="conditionalRequired" style="color: blue;"> *</span>
      <br>
      <span v-if="validation?.$error" style="color: red;">{{ messages }}</span>
    </label>
    <input
      type="text"
      :value="modelValue"
      ref="dateInput"
      class="date-input form-control"
      autocomplete="off"
      maxlength="10"
      :placeholder="placeholder || 'TT.MM.JJJJ'"
      :class="{ 'is-invalid': validation?.$error || invalidDate }"
      :disabled="disabled"
      @keydown="onKeydown"
      @blur="onBlur"
    />
    <span v-if="invalidDate" style="color: red; font-size: 0.875em;">
      Ungültiges Datum – bitte im Format TT.MM.JJJJ eingeben.
    </span>
  </div>
</template>

<script setup>
import $ from 'jquery';
import moment from 'moment';
import { defineEmits, defineProps, onMounted, ref, watch } from 'vue';

const props = defineProps({
  options: {
    type: Array,
    required: false,
  },
  classFormGroup: {
    type: String
  },
  modelValue: {
    type: String,
    default: null,
  },
  placeholder: {
    type: [ String, Boolean ],
    default: '',
  },
  label: {
    type: [ String, Boolean ],
    default: false,
  },
  validation: {
    type: Object,
    default: () => ({}),
  },
  required: {
    type: Boolean,
    default: false,
  },
  conditionalRequired:{
    type: Boolean,
    default: false,
  },
  messages: {
    type: [ Object, String ], // Adjusted to accept both Object and String
    default: () => ({}),
  },
  inputStyle: Object,
  position: {
    type: String,
    default: 'up',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit        = defineEmits([ 'update:modelValue' ]);
const dateInput   = ref(null);
const invalidDate = ref(false);

const DISPLAY_FORMAT = 'DD.MM.YYYY';
// Accepted manual input: 05.03.2024, 5.3.2024 or 05032024
const INPUT_FORMATS  = [ 'DD.MM.YYYY', 'D.M.YYYY', 'DDMMYYYY' ];
const ALLOWED_KEYS   = [ 'Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End' ];

// Returns a moment for a complete, real date (e.g. not 31.02.2024), otherwise null
const parseDate = (value) => {
  const date = moment(value, INPUT_FORMATS, true);
  return date.isValid() && date.year() >= 1900 && date.year() <= 2100 ? date : null;
};

// Block letters and other characters - only digits and dots can be typed
const onKeydown = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey || ALLOWED_KEYS.includes(e.key) || e.key.length > 1) return;
  if (!/^[0-9.]$/.test(e.key)) e.preventDefault();
};

// Text that is still not a valid date when leaving the field is cleared
const onBlur = (e) => {
  const value = e.target.value.trim();
  if (!value) {
    invalidDate.value = false;
    return;
  }

  const date = parseDate(value);
  if (date) {
    invalidDate.value = false;
    e.target.value    = date.format(DISPLAY_FORMAT);
    emit('update:modelValue', date.format(DISPLAY_FORMAT));
  } else {
    invalidDate.value = true;
    e.target.value    = '';
    emit('update:modelValue', '');
  }
};

watch(() => props.modelValue, (value) => {
  if (value && parseDate(value)) invalidDate.value = false;
});

onMounted(() => {
  $(dateInput.value).daterangepicker(
      {
        singleDatePicker: true,
        showDropdowns: true,
        locale: {
          format: 'DD.MM.YYYY',
          separator: '.',
          useCurrent: false,
          firstDay: 1,
        },
        autoApply: true,
        closeOnDateSelect: true,
        autoUpdateInput: false,
        drops: props.position,
      },
      (start) => {
        $(dateInput.value).val(start.format('DD.MM.YYYY'));
        emit('update:modelValue', start.format('DD.MM.YYYY'));
      },
  );
  $(dateInput.value).on('apply.daterangepicker', function (ev, picker) {
    if (picker.startDate) { // DISABLE DEFAULT DATE IF IS NOT SELECTED IN DATE PICKER !!!
      $(this).val(picker.startDate.format('DD.MM.YYYY'));
      emit('update:modelValue', picker.startDate.format('DD.MM.YYYY'));
    }
  });

  $(dateInput.value).on('input', function (e) {
    // Strip anything that is not a digit or dot (covers paste and mobile keyboards)
    const sanitized = e.target.value.replace(/[^0-9.]/g, '').slice(0, 10);
    if (sanitized !== e.target.value) e.target.value = sanitized;

    if (!sanitized) {
      invalidDate.value = false;
      emit('update:modelValue', '');
      return;
    }

    // Only a complete valid date goes to the model; partial input is checked on blur
    const date = parseDate(sanitized);
    if (date) {
      invalidDate.value = false;
      $(this).data('daterangepicker')?.setStartDate(date);
      emit('update:modelValue', date.format(DISPLAY_FORMAT));
    }
  });
});
</script>

<style>

</style>